import { useMachine } from "@xstate/react";
import constate from "constate";
import { useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";

import api from "common/utils/api";
import type { ID } from "common/utils/models";
import { models } from "jiffy-api";

import { highlightJiffyDesignServiceAtom } from "@design-lab/features/design-artwork/model/print-perfect.model";
import { type Side } from "@design-lab/shared";
import { useAsyncEffect } from "@design-lab/shared/hooks/useAsyncEffect";
import { useTabNavigation } from "@design-lab/shared/hooks/useTabNavigation";
import { trackDesignLabEnhancementFailed } from "@design-lab/shared/lib/analytics";
import { useDesignLabNewEnhancementTrigger } from "@design-lab/shared/lib/config";
import { useNotification } from "@design-lab/shared/ui/notification";
import { useDtfChannel } from "@design-lab/widgets/design-lab-upload-artwork/model/useDtfInit";
import { useQueryClient } from "@tanstack/react-query";

import type { Artwork } from "../types";
import { artworkMachine } from "./artwork.machine";
import { useImagesWarmFastCache } from "./useImagesFastCache";

type ArtworkMachine = {
  state: any;
  send: (event: any) => void;
};

function useArtworkCreator() {
  const { lastMessage } = useDtfChannel();
  const { setTab } = useTabNavigation();
  const setHighlight = useSetAtom(highlightJiffyDesignServiceAtom);
  const queryClient = useQueryClient();
  const shouldUseDesignLabNewEnhancementTrigger = useDesignLabNewEnhancementTrigger();
  const notification = useNotification();

  const { warmCache } = useImagesWarmFastCache();

  const markEnhancementCompleted = useCallback(
    async (machine: ArtworkMachine, artwork: Artwork, triggerEvent: string) => {
      if (machine.state.matches("finished")) return;

      if (machine.state.matches("processing.uploaded")) {
        machine.send({ type: "enhancement.started" });
      }

      machine.send({ type: "cache.warming.started" });
      const imageUrl = artwork.noBgUrl || artwork.noBgNoTrimUrl || artwork.withBgUrl;
      if (imageUrl) {
        await warmCache([imageUrl]);
      }
      machine.send({ type: "cache.warmed" });
      machine.send({
        type: "enhancement.completed",
        artwork,
        triggerEvent
      });
    },
    [warmCache]
  );

  const machineWithActions = useMemo(
    () =>
      artworkMachine.provide({
        actions: {
          selectDesignTab: ({ context }) => {
            if (context.shouldRedirect) {
              setTab(context.side);
            }
          },
          highlightJiffyDesignService: () => {
            setHighlight(true);
          },
          acknowledgeArtwork: ({ context }) => {
            if (context.artworkId !== undefined) {
              api.dtf.acknowledgeArtwork({ id: context.artworkId }).catch(() => {
                // Silently ignore errors
              });
            }
          },
          invalidateArtworkQueries: () => {
            queryClient.invalidateQueries({
              queryKey: ["dtfArtworks"]
            });
          }
        }
      }),
    [setTab, setHighlight, queryClient]
  );

  const [frontState, frontSend] = useMachine(machineWithActions, {
    input: { side: "front" }
  });
  const [backState, backSend] = useMachine(machineWithActions, {
    input: { side: "back" }
  });

  const frontMachine = useMemo(
    () => ({ state: frontState, send: frontSend }),
    [frontSend, frontState]
  );
  const backMachine = useMemo(() => ({ state: backState, send: backSend }), [backSend, backState]);

  const getMachine = useCallback(
    (id: ID | number | undefined) =>
      [frontMachine, backMachine].find(
        ({ state }) =>
          state.context.artworkId !== undefined &&
          id !== undefined &&
          String(state.context.artworkId) === String(id)
      ),
    [frontMachine, backMachine]
  );

  const getMachineBySide = useCallback(
    (side: Side) => {
      if (side === "front") {
        return frontMachine;
      }

      return backMachine;
    },
    [frontMachine, backMachine]
  );

  const getMachineByProcessTimeStamp = useCallback(
    (processTimeStamp: string) =>
      [frontMachine, backMachine].find(
        ({ state }) =>
          state.context.processTimeStamp !== undefined &&
          state.context.processTimeStamp === processTimeStamp
      ),
    [frontMachine, backMachine]
  );

  const getArtwork = useCallback(
    (id: ID | undefined) => getMachine(id)?.state.context.artwork,
    [getMachine]
  );

  const someArtworkLoading = frontState.hasTag("loading") || backState.hasTag("loading");

  useAsyncEffect(async () => {
    if (!lastMessage || !lastMessage.result) return;

    const { result, data }: any = lastMessage;

    if (result && "success" in result && result.success === false) {
      const errorArtworkId = result.artworkId || result.artwork?.id;
      const errorMessage = result.message || result.error || "Unknown error";

      const machine = getMachine(errorArtworkId);

      if (machine) {
        // Track the failure analytics
        trackDesignLabEnhancementFailed({
          artworkId: String(errorArtworkId),
          errorMessage
        });

        // Reset the machine to initial state (same as upload failure)
        machine.send({
          type: "reset",
          targetState: "initial",
          context: {
            artworkId: errorArtworkId,
            visitedStates: [],
            cacheWarmingStatus: "none",
            feedbackSelectedReason: undefined,
            shouldShowFeedback: false
          }
        });

        notification.error({
          title: "Enhancement Failed",
          description: errorMessage
        });
      }

      return;
    }

    const artworkId = result?.artworkId ?? result?.artwork?.id;
    const processTimeStamp = data?.processTimeStamp;

    if (lastMessage.action === "upload_artwork" && processTimeStamp && artworkId) {
      const machine = getMachineByProcessTimeStamp(processTimeStamp);
      if (machine && !machine.state.context.artworkId) {
        machine.send({
          type: "upload.completed",
          id: artworkId
        });
      }
      return;
    }

    const machine = getMachine(artworkId);

    if (!machine) return;

    machine.send({ type: "websocket.event", name: lastMessage.action });

    if (lastMessage.action === "process_dtf_artwork_started") {
      machine.send({ type: "enhancement.started" });
    } else if (lastMessage.action === "continue_dtf_artwork_process_started") {
      const continueDtfResult =
        lastMessage.result as models.cable.DtfChannel.continueDtfArtworkProcessStarted.Result;

      if (continueDtfResult.success && !machine.state.matches("processing")) {
        machine.send({ type: "enhancement.started" });
      }
    } else if (lastMessage.action === "extract_bg_metadata") {
      if (!shouldUseDesignLabNewEnhancementTrigger) return;

      const customResult = lastMessage.result as models.cable.DtfChannel.extractBgMetadata.Result;
      if (!customResult) return;

      await markEnhancementCompleted(machine, customResult.processData, lastMessage.action);
    } else if (lastMessage.action === "fetch_delivery_date") {
      const fetchDeliveryResult =
        lastMessage.result as models.cable.DtfChannel.fetchDeliveryDate.ActionResult;
      if (!fetchDeliveryResult.artwork) return;

      await markEnhancementCompleted(machine, fetchDeliveryResult.artwork, lastMessage.action);
    }
  }, [lastMessage, markEnhancementCompleted, shouldUseDesignLabNewEnhancementTrigger]);

  return {
    someArtworkLoading,
    front: frontMachine,
    back: backMachine,
    getArtwork,
    getMachine,
    getMachineBySide
  };
}

export const [ArtworkProvider, useArtwork] = constate(useArtworkCreator);
